import { getCookie } from '@visa/lib-cookie';
import {
  MESSAGE_DELIMITER,
  SRC_CANCEL_RESPONSE_RECEIVED,
  SRC_FAILURE_RESPONSE_RECEIVED,
  SRC_SUCCESS_RESPONSE_RECEIVED,
  SRCI_CANCEL_RESPONSE,
  SRCI_FAILURE_RESPONSE,
  SRCI_SUCCESS_RESPONSE,
  SYS_PUSH_GTM_DATA_EVENT,
  SYS_SYNC_GTM_DATA_EVENT
} from '../../src/constants/gtm-events';
import { SRC_SYSTEM_IFRAME_ID } from '../../src/constants/iframe';
import { parseGetSrcProfileIdToken } from '../../src/helpers/gtm';
import maskEmail from '../../src/helpers/mask-email';
import {
  cookieExist,
  getSsiStatus,
  getUsernameFromCookie,
  getUserType
} from '../../src-i-adapter/helpers/cookie-manager';
import getIncomingTrafficSource from '../../src-i-adapter/helpers/get-incoming-traffic-source';
import { trackINOEvent as _trackINOEvent } from '../../src-i-adapter/helpers/gtm';
import { AMEX, VISA } from '../constants/partnering-networks';
import OrchCustomData from './orch-custom-data';

const _gtmPush = data => {
  const srcSystemFrame = document.getElementById(SRC_SYSTEM_IFRAME_ID);
  if (srcSystemFrame && srcSystemFrame.contentWindow) {
    srcSystemFrame.contentWindow.postMessage(data, '*');
  }
};

const postMessageHelper = message => {
  let _parentWindow;
  if (window.opener && window !== window.opener) {
    _parentWindow = window.opener;
  } else {
    _parentWindow = window.parent;
  }

  _parentWindow.postMessage(message, '*');
};

export const trackINOEvent = (gtmEvent, data) => {
  switch (gtmEvent) {
    case SRC_CANCEL_RESPONSE_RECEIVED:
    case SRC_FAILURE_RESPONSE_RECEIVED:
    case SRC_SUCCESS_RESPONSE_RECEIVED:
    case SRCI_CANCEL_RESPONSE:
    case SRCI_FAILURE_RESPONSE:
    case SRCI_SUCCESS_RESPONSE: {
      if (data && data.src_system_name !== VISA) {
        return;
      }

      if (
        gtmEvent === SRCI_CANCEL_RESPONSE ||
        gtmEvent === SRCI_FAILURE_RESPONSE ||
        gtmEvent === SRCI_SUCCESS_RESPONSE
      ) {
        data = null;
      }

      break;
    }

    default:
      break;
  }

  data && (data.srci_name = 'VISA');
  _trackINOEvent(gtmEvent, data);
};

const _loadGtmDataFromSrci = data => {
  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(data)}`);
};

export const enrichDataLayer = data => {
  if (data && data.dataLayer) {
    const srciDataLayer = data.dataLayer;

    if (data.requestMetrics) {
      srciDataLayer.correlation_id = data.requestMetrics.correlation_id;
      srciDataLayer.incoming_traffic_source = data.requestMetrics.incomingTrafficSource;
      srciDataLayer.isnewuser = data.requestMetrics.isNewUser;
      srciDataLayer.src_system_name = data.requestMetrics.srcSystemName;
      srciDataLayer.userType = data.requestMetrics.userType;
      srciDataLayer.xo_visitid = data.requestMetrics.xo_visitid;
    }

    _loadGtmDataFromSrci(srciDataLayer);
    data.dataLayer = srciDataLayer;
  }

  return data;
};

export const getGtmData = data => {
  const gtmData = {
    src_initiator_id: OrchCustomData.apikey,
    src_system_name: data.cardBrand
  };

  if (data.dataLayer) {
    gtmData.correlation_id = data.dataLayer.correlation_id;
    gtmData.incoming_traffic_source = data.dataLayer.incoming_traffic_source;
    gtmData.isnewuser = data.dataLayer.isnewuser;
    gtmData.user_type = data.dataLayer.user_type;
    gtmData.xo_visitid = data.dataLayer.xo_visitid;
  }

  return gtmData;
};

const getBaseGtmProps = (config = {}) => {
  const visaInitData = config?.initData?.VISA;
  const visitId = visaInitData?.data?.visitId;
  const merchant_site = visaInitData?.customData?.configs?.vInitRequest?.parentUrl;
  const merchant_name =
    config?.initData?.VISA?.customData?.configs?.vInitRequest?.displayName ??
    config?.initData?.VISA?.customData?.configs?.merchantConfig?.logo?.displayName;
  return {
    api_key: config?.apikey,
    browser_protocol: window.location.protocol.split(':')[0],
    channel: 'merchant',
    correlation_id: config?.correlationId ?? 'undefined',
    experience_owner: 'SRCi',
    external_client_id: config?.externalClientId ?? 'undefined',
    flow: config?.xoFlow,
    incoming_traffic_source: getIncomingTrafficSource(),
    merchant_integration_type: visaInitData?.merchantIntegrationType ?? 'undefined',
    merchant_name: merchant_name ?? 'undefined',
    merchant_site: merchant_site ?? 'undefined',
    rememberme_type: cookieExist('ermEnabled') ? 'ERM' : 'legacy',
    src_initiator_id: VISA,
    stay_signed_in: getSsiStatus(),
    time_to_load_vsb_sdk: visaInitData?.vsbSDKLoadEnd ?? 'undefined',
    user_type: getUserType(),
    username_remembered: getCookie('alwaysRemember') === 'true',
    xo_visitid: visitId ?? 'undefined'
  };
};

let identityLookupMaskedEmail = '';
export const trackIdentityLookup = (data = {}, config = {}, vsbData = {}, network) => {
  const { isVsb, isRequest, isError } = vsbData;
  let email = data?.consumerIdentity?.identityValue;
  if (network === AMEX) {
    email = data?.identityValue;
  }
  if (typeof email === 'string') {
    identityLookupMaskedEmail = maskEmail(email);
  }
  const eventName = 'Identity lookup';
  const baseData = {
    ...getBaseGtmProps(config),
    src_email_mask: identityLookupMaskedEmail,
    src_system_name: network
  };

  const status = `${isVsb ? (isRequest ? ' Pending' : isError ? ' Error' : ' Success') : ''}`;
  const label = `${eventName}-${isVsb ? (isRequest ? 'Request ' : 'Response ') : ''}API${status}`;

  const gtmData = {
    api_name: 'identityLookup',
    event: eventName,
    event_action: eventName,
    event_label: label,
    ...baseData
  };

  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
  postMessageHelper(
    `${SYS_SYNC_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify({
      ...baseData
    })}`
  );

  return gtmData;
};

export const trackInitiateIdentityValidation = (data = {}, config = {}, vsbData = {}, network) => {
  const { isError, isRequest, isVsb } = vsbData;
  const eventName = 'Initiate identity validation';
  const merchant_country_code =
    config?.initData?.VISA?.customData?.configs?.merchantConfig?.merchantCountryCode;
  const baseData = {
    ...getBaseGtmProps(config),
    auth_provider: network,
    merchant_country_code: merchant_country_code ?? 'undefined',
    otp_delivery_channel: data?.otp_delivery_channel,
    src_email_mask: identityLookupMaskedEmail,
    src_system_name: network
  };

  const status = `${isVsb ? (isRequest ? ' Pending' : isError ? ' Error' : ' Success') : ''}`;
  const label = `${eventName}-${isVsb ? (isRequest ? 'Request ' : 'Response ') : ''}API${status}`;

  const gtmData = {
    api_name: 'initiateIdentityValidation',
    event: eventName,
    event_action: eventName,
    event_label: label,
    ...baseData
  };

  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
  postMessageHelper(
    `${SYS_SYNC_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify({
      ...baseData
    })}`
  );

  return gtmData;
};

export const trackCompleteIdentityValidation = (config = {}, vsbData = {}, network) => {
  const { isError, isRequest, isVsb } = vsbData;
  const eventName = 'Identity validation response';
  const baseData = {
    ...getBaseGtmProps(config),
    auth_provider: network,
    merchant_country_code:
      config?.initData?.VISA?.customData?.configs?.merchantConfig?.merchantCountryCode ??
      'undefined',
    src_email_mask: identityLookupMaskedEmail,
    src_system_name: network
  };

  const status = `${isVsb ? (isRequest ? ' Pending' : isError ? ' Error' : ' Success') : ''}`;
  const label = `${eventName}-API${status}`;

  const gtmData = {
    api_name: 'completeIdentityValidation',
    event: eventName,
    event_action: eventName,
    event_label: label,
    ...baseData
  };

  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
  postMessageHelper(
    `${SYS_SYNC_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify({
      ...baseData
    })}`
  );

  return gtmData;
};

const getUserTypeIdentification = (isRequest, recognized, usedDevice) => {
  let userTypeIdentification = 'Unrecognized user';

  if (recognized && !isRequest) {
    userTypeIdentification = 'Recognized user';
  } else if (!isRequest && usedDevice) {
    userTypeIdentification = 'Unrecognized Returning user';
  }
  return userTypeIdentification;
};

export const trackRecognizedUser = (data = {}, config = {}, vsbData = {}, recognized = true) => {
  const { isError, isRequest, usedDevice } = vsbData;
  const srcName = Object.keys(data)[0];
  let parsedToken = {};

  if (
    Array.isArray(data[srcName]?.idTokens?.idTokens) ||
    Array.isArray(data[srcName]?.idTokens) ||
    (typeof data[srcName]?.idTokens === 'string' && data[srcName]?.idTokens.length > 0)
  ) {
    let token = data[srcName]?.idTokens;
    if (Array.isArray(data[srcName]?.idTokens)) {
      token = data[srcName]?.idTokens[0];
    } else if (Array.isArray(data[srcName]?.idTokens?.idTokens)) {
      token = data[srcName]?.idTokens?.idTokens[0];
    }

    parsedToken = parseGetSrcProfileIdToken(token);
  }

  const userTypeIdentification = getUserTypeIdentification(isRequest, recognized, usedDevice);
  const eventName = 'User type identification';
  const baseData = {
    ...getBaseGtmProps(config),
    auth_provider: srcName,
    merchant_country_code:
      config?.initData?.VISA?.customData?.configs?.merchantConfig?.merchantCountryCode ??
      'undefined',
    nvr: isRequest || srcName === VISA ? false : data[srcName]?.recognized,
    src_email_mask: parsedToken?.src_email_mask ?? getUsernameFromCookie(),
    src_system_name: srcName,
    user_type_identification: userTypeIdentification,
    vat: Boolean(data?.VISA?.recognized),
    vr: userTypeIdentification === 'Recognized user'
  };

  //TODO:
  //1: add VAT and VR for VISA request/response
  //2: add NVR for non-VISA request/response

  if (config.isVsb && recognized) {
    baseData.user_type = 'Recognized';
  }

  const status = `${isRequest ? ' Pending' : isError ? ' Error' : ' Success'}`;
  const label = `${eventName}-${isRequest ? 'Request ' : 'Response '}API${status}`;

  const gtmData = {
    api_name: 'isRecognized',
    event: eventName,
    event_action: eventName,
    event_label: label,
    ...baseData
  };

  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
  postMessageHelper(
    `${SYS_SYNC_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify({
      ...baseData
    })}`
  );

  return gtmData;
};

export const trackVisaUnrecognizedReturningUser = (data = {}, config = {}) => {
  const isRecognizedData = config?.srcSdks?.apiResponseCache?.isRecognized ?? {};
  const filterRecognizedData = Object.keys(isRecognizedData).reduce((accum, key) => {
    if (isRecognizedData[key].recognized) {
      accum.push(key);
    }
    return accum;
  }, []);

  if (filterRecognizedData.length > 0) {
    return null;
  }

  const srcName = config?.srcSdks?.orchConfig?.preferredNetwork;
  let parsedToken = {};

  if (Array.isArray(data) || (typeof data === 'string' && data.length > 0)) {
    let token = data;
    if (Array.isArray(data)) {
      token = data[0];
    }

    parsedToken = parseGetSrcProfileIdToken(token);
  }

  const iss = (parsedToken?.iss ?? '').toString().toLowerCase();
  const eventName = 'User type identification';
  const baseData = {
    ...getBaseGtmProps(config),
    auth_provider: srcName,
    merchant_country_code:
      config?.initData?.VISA?.customData?.configs?.merchantConfig?.merchantCountryCode ??
      'undefined',
    nvr: !iss.includes('visa'),
    src_email_mask: parsedToken?.src_email_mask ?? 'undefined',
    src_system_name: srcName,
    user_type_identification: 'Unrecognized Returning user',
    vat: iss.includes('visa'),
    vr: false
  };

  const gtmData = {
    api_name: 'getSrcProfile',
    event: eventName,
    event_action: eventName,
    event_label: `${eventName}-API`,
    ...baseData
  };

  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
  postMessageHelper(
    `${SYS_SYNC_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify({
      ...baseData
    })}`
  );

  return gtmData;
};

export const trackVsbInitialize = (configs = {}, isError) => {
  const status = isError ? 'Error' : 'Success';
  const baseData = {
    ...getBaseGtmProps(configs)
  };
  baseData.merchant_name = configs?.currVInitConfigs?.displayName;
  const gtmData = {
    event: 'VSB_Initialize',
    event_action: 'VSB Initialize',
    event_category: 'VSB Initialize',
    event_label: `VSB Initialize-Response API ${status}`,
    ...baseData
  };

  _gtmPush(`${SYS_PUSH_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify(gtmData)}`);
  postMessageHelper(
    `${SYS_SYNC_GTM_DATA_EVENT}${MESSAGE_DELIMITER}${JSON.stringify({
      ...baseData
    })}`
  );
};
